/**
 * Event statistics engine — pure aggregation over the planner state for the
 * StatsModal + CapacityPanel. Turns inquiries + placements + the pricing rate
 * card into financial (revenue captured vs lost) and operational metrics,
 * scoped to a time period. No React, no store — trivially testable, mirrors the
 * `engine/capacity.ts` style.
 */
import type { AircraftCatalog, ArrivalInquiry, EventPlacement, OperatorClass, ProgramMembership, ZoneGeometry } from './types'
import { type Tier, TIER_ORDER, TIER_LABELS, tierForCategory } from './tiers'
import { concurrencyHistogram, peakConcurrency, estimateZoneCapacity } from './capacity'
import { parseFuelRequest } from '../motion/fuelRequest'
import {
  type EventPricing,
  type RevenueBreakdown,
  type ZonePricingOverride,
  resolveAircraftRevenue,
  addBreakdown,
  emptyBreakdown,
} from '../data/eventPricing'

const DAY_MS = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000
/** Stays shorter than this read as same-day quick turns. */
const QUICK_TURN_HOURS = 8
/** Illustrative line-crew ratio: one marshaller team per N concurrent aircraft. */
const AIRCRAFT_PER_CREW = 6

export interface PeriodRange {
  start: number
  end: number
}

export interface NamedValue {
  label: string
  value: number
}

export interface FlowPoint {
  t: number
  arrivals: number
  departures: number
  onField: number
}

export interface ZoneUtil {
  areaId: string
  name: string
  capacity: number
  peak: number
  pct: number
}

export interface EventStats {
  period: PeriodRange
  // ── Financial ──
  potential: RevenueBreakdown
  captured: RevenueBreakdown
  lost: RevenueBreakdown
  turnAways: number
  placedCount: number
  demandCount: number
  revenueByTier: NamedValue[]
  revenueByClientGroup: NamedValue[]
  revenueByProgram: NamedValue[]
  avgRevenuePerAircraft: number
  // ── Operational ──
  arrivalsInPeriod: number
  peakConcurrent: number
  fleetMixByTier: NamedValue[]
  jetAGallons: number
  avgasGallons: number
  fuelTruckDispatches: number
  flowCurve: FlowPoint[]
  busiestHour: number
  avgDwellHours: number
  quickTurnPct: number
  triage: { green: number; amber: number; red: number }
  vipCount: number
  serviceDemand: NamedValue[]
  zoneUtil: ZoneUtil[]
  lineCrewAtPeak: number
}

const inPeriod = (t: number, p: PeriodRange) => t >= p.start && t <= p.end

/** Count UTC midnights the aircraft is on the ground (arrival ≤ M < departure) that fall in the period. */
function nightsInPeriod(arrival: number, departure: number, p: PeriodRange): number {
  let m = Math.ceil(arrival / DAY_MS) * DAY_MS
  let count = 0
  for (; m < departure && m <= p.end; m += DAY_MS) {
    if (m >= p.start && m >= arrival) count++
  }
  return count
}

export interface ComputeStatsArgs {
  inquiriesById: Record<string, ArrivalInquiry>
  placements: Record<string, EventPlacement>
  catalog: AircraftCatalog
  pricing: EventPricing
  zoneOverrides: Record<string, ZonePricingOverride>
  zones: ZoneGeometry[]
  period: PeriodRange
}

export function computeEventStats(args: ComputeStatsArgs): EventStats {
  const { inquiriesById, placements, catalog, pricing, zoneOverrides, zones, period } = args
  const inquiries = Object.values(inquiriesById)
  const placedList = Object.values(placements)
  const placedBySchedule = new Map<string, EventPlacement>()
  for (const p of placedList) placedBySchedule.set(p.scheduleId, p)

  const captured = emptyBreakdown()
  const lost = emptyBreakdown()
  const revByTier = new Map<Tier, number>()
  const revByGroup = new Map<OperatorClass, number>()
  const revByProgram = new Map<ProgramMembership, number>()

  const fleetByTier = new Map<Tier, number>()
  const serviceCounts = new Map<string, number>()
  const triage = { green: 0, amber: 0, red: 0 }
  let jetAGallons = 0
  let avgasGallons = 0
  let fuelTruckDispatches = 0
  let vipCount = 0
  let arrivalsInPeriod = 0
  let dwellSum = 0
  let quickTurns = 0
  let turnAways = 0

  for (const inq of inquiries) {
    const entry = catalog.get(inq.typeCode)
    const tier = tierForCategory(entry?.category)
    const fuel = parseFuelRequest(inq.services)
    const includeOneTime = inPeriod(inq.arrival, period)
    const nights = nightsInPeriod(inq.arrival, inq.departure, period)
    const placement = placedBySchedule.get(inq.scheduleId)

    const breakdown = resolveAircraftRevenue(
      inq,
      entry,
      fuel,
      pricing,
      placement ? zoneOverrides[placement.areaId] : undefined,
      { nights, includeOneTime }
    )

    if (placement) {
      addBreakdown(captured, breakdown)
      revByTier.set(tier, (revByTier.get(tier) ?? 0) + breakdown.total)
      revByGroup.set(inq.operatorClass, (revByGroup.get(inq.operatorClass) ?? 0) + breakdown.total)
      revByProgram.set(inq.program, (revByProgram.get(inq.program) ?? 0) + breakdown.total)
    } else {
      addBreakdown(lost, breakdown)
      turnAways++
    }

    // Operational counts are demand-side, scoped to arrivals within the period.
    if (includeOneTime) {
      arrivalsInPeriod++
      fleetByTier.set(tier, (fleetByTier.get(tier) ?? 0) + 1)
      if (inq.isVip) vipCount++
      triage[inq.triage]++
      dwellSum += (inq.departure - inq.arrival) / HOUR_MS
      if ((inq.departure - inq.arrival) / HOUR_MS < QUICK_TURN_HOURS) quickTurns++
      if (fuel) {
        fuelTruckDispatches++
        if (fuel.fuelType === 'avgas') avgasGallons += fuel.gallons
        else jetAGallons += fuel.gallons
      }
      for (const svc of inq.services) {
        serviceCounts.set(svc.type, (serviceCounts.get(svc.type) ?? 0) + 1)
      }
    }
  }

  const potential = addBreakdown(addBreakdown(emptyBreakdown(), captured), lost)
  const placedCount = placedList.length

  // Peak concurrent across the period (placed aircraft).
  const hist = concurrencyHistogram(placedList, period.start, period.end, 200)
  const peakConcurrent = peakConcurrency(hist).count

  // Per-zone utilization (period-scoped peak ÷ capacity).
  const zoneUtil: ZoneUtil[] = zones.map((z) => {
    const zRanges = placedList.filter((p) => p.areaId === z.areaId)
    const zHist = concurrencyHistogram(zRanges, period.start, period.end, 120)
    const peak = peakConcurrency(zHist).count
    const capacity = estimateZoneCapacity(z)
    return {
      areaId: z.areaId,
      name: z.name,
      capacity,
      peak,
      pct: capacity > 0 ? Math.min(100, Math.round((peak / capacity) * 100)) : 0,
    }
  })

  // Flow curve: arrivals + departures (demand) and on-field (placed) over the period.
  const BUCKETS = 48
  const step = (period.end - period.start) / BUCKETS || 1
  const flowCurve: FlowPoint[] = []
  for (let i = 0; i < BUCKETS; i++) {
    const t = period.start + i * step
    const next = t + step
    let arrivals = 0
    let departures = 0
    let onField = 0
    for (const inq of inquiries) {
      if (inq.arrival >= t && inq.arrival < next) arrivals++
      if (inq.departure >= t && inq.departure < next) departures++
    }
    for (const p of placedList) if (p.arrival <= t && t < p.departure) onField++
    flowCurve.push({ t, arrivals, departures, onField })
  }
  const busiest = flowCurve.reduce((best, p) => (p.arrivals > best.arrivals ? p : best), flowCurve[0] ?? { t: period.start, arrivals: 0, departures: 0, onField: 0 })

  const named = <K extends string>(m: Map<K, number>, labels: Record<K, string>, order: K[]): NamedValue[] =>
    order.filter((k) => (m.get(k) ?? 0) > 0).map((k) => ({ label: labels[k], value: m.get(k) ?? 0 }))

  return {
    period,
    potential,
    captured,
    lost,
    turnAways,
    placedCount,
    demandCount: inquiries.length,
    revenueByTier: named(revByTier, TIER_LABELS, TIER_ORDER),
    revenueByClientGroup: named(revByGroup, GROUP_LABELS, ['fractional', 'management', 'individual']),
    revenueByProgram: named(revByProgram, PROGRAM_LABELS_LOCAL, ['contract_fuel', 'loyalty', 'based_tenant', 'transient']),
    avgRevenuePerAircraft: placedCount > 0 ? captured.total / placedCount : 0,
    arrivalsInPeriod,
    peakConcurrent,
    fleetMixByTier: named(fleetByTier, TIER_LABELS, TIER_ORDER),
    jetAGallons,
    avgasGallons,
    fuelTruckDispatches,
    flowCurve,
    busiestHour: busiest.t,
    avgDwellHours: arrivalsInPeriod > 0 ? dwellSum / arrivalsInPeriod : 0,
    quickTurnPct: arrivalsInPeriod > 0 ? Math.round((quickTurns / arrivalsInPeriod) * 100) : 0,
    triage,
    vipCount,
    serviceDemand: [...serviceCounts.entries()]
      .map(([type, count]) => ({ label: SERVICE_DEMAND_LABELS[type] ?? type, value: count }))
      .sort((a, b) => b.value - a.value),
    zoneUtil: zoneUtil.sort((a, b) => b.pct - a.pct),
    lineCrewAtPeak: Math.ceil(peakConcurrent / AIRCRAFT_PER_CREW),
  }
}

// Local label maps (kept here so the engine has no UI dependency).
const GROUP_LABELS: Record<OperatorClass, string> = {
  fractional: 'Fractional',
  management: 'Management Co.',
  individual: 'Individual',
}
const PROGRAM_LABELS_LOCAL: Record<ProgramMembership, string> = {
  transient: 'Transient',
  contract_fuel: 'Contract Fuel',
  based_tenant: 'Based Tenant',
  loyalty: 'Loyalty',
}
const SERVICE_DEMAND_LABELS: Record<string, string> = {
  fuel: 'Fuel',
  gpu: 'GPU',
  lav: 'Lav Service',
  catering: 'Catering',
  rental_car: 'Rental Car',
  hangar_request: 'Hangar Request',
  de_ice: 'De-Ice',
}
